import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { 
  Activity, Play, RefreshCw, XCircle, Search, TrendingUp, TrendingDown,
  BarChart3, Clock, CheckCircle, AlertTriangle, TestTube, Settings,
  Target, Database, Zap, Trash2, Download, Shield, DollarSign, 
  ArrowUpRight, ArrowDownRight, Building2
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const KensysTargetsPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [state, setState] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data states
  const [targetPrices, setTargetPrices] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchSymbol, setSearchSymbol] = useState("");
  const [searchSector, setSectorFilter] = useState("");
  const [minUpside, setMinUpside] = useState(null);
  
  // Run history & logs
  const [runHistory, setRunHistory] = useState([]);
  const [runLogs, setRunLogs] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  
  // Preflight
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  
  // Observation dates
  const [obsDates, setObsDates] = useState(null);
  const [availableDates, setAvailableDates] = useState(null);
  const [selectedObsDate, setSelectedObsDate] = useState("");
  const [obsDateFilter, setObsDateFilter] = useState(null);
  const [testSymbol, setTestSymbol] = useState("AAPL"); // Default test symbol

  // Dynamic Upside states
  const [dynamicUpsideStats, setDynamicUpsideStats] = useState(null);
  const [dynamicUpsideData, setDynamicUpsideData] = useState([]);
  const [dynamicUpsideLoading, setDynamicUpsideLoading] = useState(false);
  const [dynamicUpsideRunning, setDynamicUpsideRunning] = useState(false);
  const [dynamicUpsideDates, setDynamicUpsideDates] = useState([]);
  const [priceFetchStatus, setPriceFetchStatus] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [tickerSearch, setTickerSearch] = useState("");
  const [tickerHistory, setTickerHistory] = useState([]);
  const [tickerHistoryLoading, setTickerHistoryLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/kensys-targets/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [token]);

  const fetchState = useCallback(async () => {
    try {
      const response = await fetch(`${API}/kensys-targets/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setState(result);
        setRunning(result?.status === "running");
      }
    } catch (error) {
      console.error("Failed to fetch state:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API}/kensys-targets/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setConfig(result);
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      let url = `${API}/kensys-targets/data?limit=100`;
      if (obsDateFilter) url += `&observation_date=${obsDateFilter}`;
      if (searchSymbol) url += `&symbol=${searchSymbol.toUpperCase()}`;
      if (searchSector) url += `&sector=${encodeURIComponent(searchSector)}`;
      if (minUpside !== null) url += `&min_upside=${minUpside}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setTargetPrices(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setDataLoading(false);
    }
  }, [token, obsDateFilter, searchSymbol, searchSector, minUpside]);

  const fetchRunHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API}/kensys-targets/runs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setRunHistory(result.runs || []);
        if (result.runs?.length > 0 && !selectedRunId) {
          setSelectedRunId(result.runs[0].run_id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch run history:", error);
    }
  }, [token, selectedRunId]);

  const fetchRunLogs = useCallback(async (runId) => {
    if (!runId) return;
    try {
      const response = await fetch(`${API}/kensys-targets/runs/${runId}/logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setRunLogs(result.logs || []);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  }, [token]);

  const fetchObsDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/kensys-targets/observation-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setObsDates(result);
      }
    } catch (error) {
      console.error("Failed to fetch observation dates:", error);
    }
  }, [token]);

  const fetchAvailableDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/kensys-targets/available-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setAvailableDates(result);
      }
    } catch (error) {
      console.error("Failed to fetch available dates:", error);
    }
  }, [token]);

  // Dynamic Upside functions
  const fetchDynamicUpsideStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/dynamic-upside/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setDynamicUpsideStats(result);
      }
    } catch (error) {
      console.error("Failed to fetch dynamic upside stats:", error);
    }
  }, [token]);

  const fetchDynamicUpsideData = useCallback(async () => {
    setDynamicUpsideLoading(true);
    try {
      const response = await fetch(`${API}/dynamic-upside/table?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setDynamicUpsideData(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch dynamic upside data:", error);
    } finally {
      setDynamicUpsideLoading(false);
    }
  }, [token]);

  const fetchDynamicUpsideDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/dynamic-upside/dates?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setDynamicUpsideDates(result.dates || []);
      }
    } catch (error) {
      console.error("Failed to fetch dynamic upside dates:", error);
    }
  }, [token]);

  const fetchTickerHistory = useCallback(async (symbol) => {
    if (!symbol) {
      setTickerHistory([]);
      return;
    }
    setTickerHistoryLoading(true);
    try {
      const response = await fetch(`${API}/dynamic-upside/symbol/${symbol.toUpperCase()}?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setTickerHistory(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch ticker history:", error);
      setTickerHistory([]);
    } finally {
      setTickerHistoryLoading(false);
    }
  }, [token]);

  const handleRunDynamicUpside = async (force = false) => {
    setDynamicUpsideRunning(true);
    try {
      const response = await fetch(`${API}/dynamic-upside/run?force=${force}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (response.ok) {
        if (result.status === "success") {
          toast.success(`Computed ${result.records_computed} dynamic upside records (${result.prices_from_fmp} prices from FMP)`);
        } else if (result.status === "skipped") {
          toast.info("Data already exists for today. Use Force to recompute.");
        } else {
          toast.warning(result.message || "Completed with warnings");
        }
        fetchDynamicUpsideStats();
        fetchDynamicUpsideData();
        fetchDynamicUpsideDates();
        fetchPriceFetchStatus();
      } else {
        toast.error(result.detail || result.error || "Failed to run dynamic upside");
      }
    } catch (error) {
      toast.error("Failed to run dynamic upside");
    } finally {
      setDynamicUpsideRunning(false);
    }
  };

  const fetchPriceFetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/dynamic-upside/price-fetch-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setPriceFetchStatus(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch price status:", error);
    }
  }, [token]);

  const handleComputeFullDynamicUpside = async (force = false) => {
    const msg = force 
      ? "This will force recompute dynamic upside for all dates. Continue?"
      : "This will compute dynamic upside for all dates. Continue?";
    
    if (!window.confirm(msg)) {
      return;
    }
    
    setBackfillRunning(true);
    try {
      // Call incremental - only compute missing dates (or full if no data exists)
      const response = await fetch(`${API}/dynamic-upside/incremental`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (response.ok) {
        if (result.status === "up_to_date") {
          toast.success("Dynamic upside is already up to date!");
          setBackfillRunning(false);
        } else {
          toast.success(`Computing ${result.mode === "incremental" ? "new dates" : "full history"}...`);
          // Poll for completion
          setTimeout(() => {
            fetchDynamicUpsideStats();
            fetchDynamicUpsideDates();
          }, 5000);
          setTimeout(() => {
            fetchDynamicUpsideStats();
            fetchDynamicUpsideDates();
            setBackfillRunning(false);
          }, 25000);
        }
      } else {
        toast.error(result.detail || "Failed to start computation");
        setBackfillRunning(false);
      }
    } catch (error) {
      toast.error("Failed to start dynamic upside computation");
      setBackfillRunning(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchState();
    fetchConfig();
    fetchRunHistory();
    fetchObsDates();
    fetchAvailableDates();
    fetchDynamicUpsideStats();
    fetchDynamicUpsideDates();
    
    const interval = setInterval(() => {
      fetchState();
      if (running) fetchStats();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [fetchStats, fetchState, fetchConfig, fetchRunHistory, fetchObsDates, fetchAvailableDates, fetchDynamicUpsideStats, fetchDynamicUpsideDates, running]);

  useEffect(() => {
    if (activeTab === "data") {
      fetchData();
    } else if (activeTab === "logs" && selectedRunId) {
      fetchRunLogs(selectedRunId);
    } else if (activeTab === "history") {
      fetchRunHistory();
    } else if (activeTab === "dynamic") {
      fetchDynamicUpsideData();
      fetchDynamicUpsideStats();
      fetchDynamicUpsideDates();
      fetchPriceFetchStatus();
    }
  }, [activeTab, fetchData, fetchRunLogs, fetchRunHistory, fetchDynamicUpsideData, fetchDynamicUpsideStats, fetchDynamicUpsideDates, fetchPriceFetchStatus, selectedRunId]);

  // Auto-refresh logs while running
  useEffect(() => {
    if (running && activeTab === "logs" && selectedRunId) {
      const interval = setInterval(() => fetchRunLogs(selectedRunId), 3000);
      return () => clearInterval(interval);
    }
  }, [running, activeTab, selectedRunId, fetchRunLogs]);

  const handleRunTest = async () => {
    if (!testSymbol.trim()) {
      toast.error("Please enter a test symbol (e.g., AAPL)");
      return;
    }
    
    try {
      let params = [];
      params.push(`symbols=${testSymbol.trim().toUpperCase()}`);
      if (selectedObsDate) params.push(`observation_date=${selectedObsDate}`);
      const queryStr = `?${params.join("&")}`;
      
      const response = await fetch(`${API}/kensys-targets/run/test${queryStr}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`Test: ${testSymbol.toUpperCase()}${selectedObsDate ? ` @ ${selectedObsDate}` : ""}`);
        setRunning(true);
        setActiveTab("logs");
        fetchState();
        fetchRunHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start test");
      }
    } catch (error) {
      toast.error("Failed to start test pipeline");
    }
  };

  const handleRunFull = async () => {
    try {
      const params = selectedObsDate ? `?observation_date=${selectedObsDate}` : "";
      const response = await fetch(`${API}/kensys-targets/run/full${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`Full pipeline started${selectedObsDate ? ` for ${selectedObsDate}` : ""}`);
        setRunning(true);
        setActiveTab("logs");
        fetchState();
        fetchRunHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start");
      }
    } catch (error) {
      toast.error("Failed to start full pipeline");
    }
  };

  const handleCancel = async () => {
    try {
      const response = await fetch(`${API}/kensys-targets/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Cancel signal sent");
        fetchState();
      }
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const handleClearData = async () => {
    if (!confirm("Delete ALL computed target price data? This cannot be undone.")) return;
    
    try {
      const response = await fetch(`${API}/kensys-targets/data`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Deleted ${result.deleted_count} records`);
        fetchStats();
        fetchData();
      } else {
        toast.error("Failed to clear data");
      }
    } catch (error) {
      toast.error("Failed to clear data");
    }
  };

  const runPreflight = async () => {
    setPreflightLoading(true);
    try {
      const response = await fetch(`${API}/kensys-targets/preflight`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setPreflight(result);
        if (result.status === "ready") {
          toast.success("All prerequisites met!");
        } else {
          toast.warning(`Found ${result.issues?.length || 0} issue(s)`);
        }
      }
    } catch (error) {
      toast.error("Failed to run preflight check");
    } finally {
      setPreflightLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "N/A";
    const pct = Number(num) * 100;
    return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
  };

  const formatPrice = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "N/A";
    return `$${Number(num).toFixed(2)}`;
  };

  if (loading || statsLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-6 h-6 text-primary" />
            Kensys Target Prices
          </h1>
          <p className="text-muted-foreground">
            Proprietary target prices combining fund intelligence with analyst consensus
          </p>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <>
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                <Activity className="w-3 h-3 mr-1 animate-pulse" />
                Running
              </Badge>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <XCircle className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            </>
          ) : (
            <>
              {/* Test Symbol Input */}
              <div className="flex items-center gap-1 border rounded-md px-2 py-1 bg-background">
                <TestTube className="w-4 h-4 text-muted-foreground" />
                <Input
                  className="w-20 h-7 border-0 p-0 text-sm font-mono focus-visible:ring-0"
                  placeholder="AAPL"
                  value={testSymbol}
                  onChange={(e) => setTestSymbol(e.target.value.toUpperCase())}
                  data-testid="test-symbol-input"
                />
              </div>
              
              {/* Observation Date Selector */}
              <select
                className="p-2 rounded-md bg-background border text-sm min-w-[150px]"
                value={selectedObsDate}
                onChange={(e) => setSelectedObsDate(e.target.value)}
                data-testid="obs-date-select"
              >
                <option value="">Today (latest)</option>
                {availableDates?.curves_dates?.map((date) => (
                  <option 
                    key={date} 
                    value={date}
                    className={availableDates?.already_computed?.includes(date) ? "text-muted-foreground" : ""}
                  >
                    {date} {availableDates?.already_computed?.includes(date) ? "✓" : ""}
                  </option>
                ))}
              </select>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRunTest}
                disabled={!testSymbol.trim()}
                data-testid="run-test-btn"
              >
                <TestTube className="w-4 h-4 mr-1" />
                Test
              </Button>
              <Button 
                size="sm" 
                onClick={handleRunFull}
                data-testid="run-full-btn"
              >
                <Play className="w-4 h-4 mr-1" />
                Full Run
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {running && state?.progress && (
        <Card className="border-yellow-500/20 bg-yellow-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{state.progress.phase}</span>
              <span className="text-sm text-muted-foreground">
                {state.progress.percent}%
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-yellow-500 transition-all duration-500"
                style={{ width: `${state.progress.percent}%` }}
              />
            </div>
            {state.progress.batch && (
              <p className="text-xs text-muted-foreground mt-1">
                Batch: {state.progress.batch}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data">Target Prices</TabsTrigger>
          <TabsTrigger value="dynamic" data-testid="tab-dynamic">Dynamic Upside</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Run History</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Records</p>
                    <p className="text-2xl font-bold">{stats?.total_records?.toLocaleString() || 0}</p>
                  </div>
                  <Database className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Unique Symbols</p>
                    <p className="text-2xl font-bold">{stats?.unique_symbols?.toLocaleString() || 0}</p>
                  </div>
                  <Target className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Upside</p>
                    <p className="text-2xl font-bold">
                      {stats?.upside_stats?.avg != null ? `${stats.upside_stats.avg}%` : "N/A"}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Last Run</p>
                    <p className="text-lg font-medium">
                      {state?.last_run_status || "Never"}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-primary/20" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Observation Dates Card */}
          {obsDates && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Data Timeline
                </CardTitle>
                <CardDescription>
                  Observation dates available in source collections
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {obsDates.source_data?.holdings_enriched && (
                    <div className="p-3 bg-muted/30 rounded">
                      <p className="text-xs text-muted-foreground">Holdings (13F Filings)</p>
                      <p className="text-sm font-mono">
                        {obsDates.source_data.holdings_enriched.oldest || "N/A"} → {obsDates.source_data.holdings_enriched.newest || "N/A"}
                      </p>
                    </div>
                  )}
                  {obsDates.source_data?.fund_sector_skill_12m && (
                    <div className="p-3 bg-muted/30 rounded">
                      <p className="text-xs text-muted-foreground">Fund Skills</p>
                      <p className="text-sm font-mono">
                        {obsDates.source_data.fund_sector_skill_12m.oldest || "N/A"} → {obsDates.source_data.fund_sector_skill_12m.newest || "N/A"}
                      </p>
                    </div>
                  )}
                  {obsDates.source_data?.fund_performance_curves && (
                    <div className="p-3 bg-muted/30 rounded">
                      <p className="text-xs text-muted-foreground">Curves / Gradients</p>
                      <p className="text-sm font-mono">
                        {obsDates.source_data.fund_performance_curves.oldest || "N/A"} → {obsDates.source_data.fund_performance_curves.newest || "N/A"}
                      </p>
                    </div>
                  )}
                  {obsDates.source_data?.price_target_daily && (
                    <div className="p-3 bg-muted/30 rounded">
                      <p className="text-xs text-muted-foreground">Price Targets (Analyst)</p>
                      <p className="text-sm font-mono">
                        {obsDates.source_data.price_target_daily.oldest || "N/A"} → {obsDates.source_data.price_target_daily.newest || "N/A"}
                      </p>
                    </div>
                  )}
                </div>
                {obsDates.kensys_target_prices?.length > 0 && (
                  <div className="mt-4 p-3 bg-primary/10 rounded border border-primary/20">
                    <p className="text-xs text-muted-foreground mb-1">Computed Kensys Target Dates</p>
                    <div className="flex flex-wrap gap-2">
                      {obsDates.kensys_target_prices.slice(0, 10).map((date, idx) => (
                        <Badge key={idx} variant="outline">{date}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Run Schedule Card */}
          {availableDates && (availableDates.curves_dates?.length > 0 || availableDates.skills_dates?.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Run Schedule
                </CardTitle>
                <CardDescription>
                  Available observation dates from curves pipeline. Select a date to run.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Quick Test Section */}
                <div className="p-4 bg-muted/30 rounded-lg border">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <TestTube className="w-4 h-4" />
                    Quick Test (Single Symbol)
                  </h4>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">Symbol:</Label>
                      <Input
                        className="w-24 h-8 font-mono text-sm"
                        placeholder="AAPL"
                        value={testSymbol}
                        onChange={(e) => setTestSymbol(e.target.value.toUpperCase())}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">Date:</Label>
                      <select
                        className="h-8 px-2 rounded-md bg-background border text-sm"
                        value={selectedObsDate}
                        onChange={(e) => setSelectedObsDate(e.target.value)}
                      >
                        <option value="">Today</option>
                        {availableDates?.curves_dates?.map((date) => (
                          <option key={date} value={date}>{date}</option>
                        ))}
                      </select>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleRunTest}
                      disabled={!testSymbol.trim() || running}
                    >
                      <TestTube className="w-4 h-4 mr-1" />
                      Run Test
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Tests the pipeline for a single symbol to verify data and calculations before running full batch.
                  </p>
                </div>

                {/* Dates Grid */}
                <div>
                  <h4 className="text-sm font-semibold mb-3">13F Observation Dates</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Aligned to 13F filing cycle (2 days after deadline): Feb 16, May 17, Aug 16, Nov 16
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {availableDates.curves_dates?.map((date) => {
                      const isComputed = availableDates.already_computed?.includes(date);
                      const isSelected = selectedObsDate === date;
                      return (
                        <div
                          key={date}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? "border-primary bg-primary/10" 
                              : isComputed 
                                ? "border-green-500/30 bg-green-500/5" 
                                : "border-border hover:border-primary/50"
                          }`}
                          onClick={() => setSelectedObsDate(date)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm">{date}</span>
                            {isComputed && <CheckCircle className="w-4 h-4 text-green-500" />}
                          </div>
                          {isSelected && (
                            <Button 
                              size="sm" 
                              className="w-full mt-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRunFull();
                              }}
                              disabled={running}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Run Full
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  <CheckCircle className="w-3 h-3 inline mr-1 text-green-500" />
                  = Already computed. Click to select, then run to update.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Prerequisites Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Prerequisites Check
                  </CardTitle>
                  <CardDescription>
                    Verify data sources before running the pipeline
                  </CardDescription>
                </div>
                <Button 
                  onClick={runPreflight} 
                  disabled={preflightLoading}
                  variant="outline"
                  data-testid="preflight-btn"
                >
                  {preflightLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  Run Check
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {preflight ? (
                <div className="space-y-4">
                  {/* Status Banner - show warning style if there are warnings even when ready */}
                  <div className={`p-4 rounded-lg ${
                    preflight.status === "ready" && !preflight.warnings?.length
                      ? "bg-green-500/10 border border-green-500/30" 
                      : preflight.status === "ready" && preflight.warnings?.length
                        ? "bg-yellow-500/10 border border-yellow-500/30"
                        : "bg-red-500/10 border border-red-500/30"
                  }`}>
                    <div className="flex items-center gap-2">
                      {preflight.status === "ready" && !preflight.warnings?.length ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : preflight.status === "ready" && preflight.warnings?.length ? (
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className={`font-semibold ${
                        preflight.status === "ready" && !preflight.warnings?.length
                          ? "text-green-400" 
                          : preflight.status === "ready" && preflight.warnings?.length
                            ? "text-yellow-400"
                            : "text-red-400"
                      }`}>
                        {preflight.summary || "Check completed"}
                      </span>
                    </div>
                  </div>

                  {preflight.issues?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-red-400">Issues</h4>
                      {preflight.issues.map((issue, idx) => (
                        <div key={idx} className="p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
                          {issue}
                        </div>
                      ))}
                    </div>
                  )}

                  {preflight.warnings?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-yellow-400">Warnings</h4>
                      {preflight.warnings.map((warn, idx) => (
                        <div key={idx} className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-sm text-yellow-300">
                          {warn}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Data Counts */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                    {Object.entries(preflight.checks || {}).map(([key, val]) => (
                      <div key={key} className="p-3 bg-muted/30 rounded">
                        <p className="text-xs text-muted-foreground truncate">{key}</p>
                        <p className="font-mono text-sm">{val?.count?.toLocaleString() || 0}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {stats?.prerequisites && Object.entries(stats.prerequisites).map(([key, count]) => (
                    <div key={key} className={`p-3 rounded ${
                      count > 0 ? "bg-green-500/10" : "bg-red-500/10"
                    }`}>
                      <p className="text-xs text-muted-foreground truncate">{key}</p>
                      <p className="font-mono text-sm">{count?.toLocaleString() || 0}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Algorithm Explanation */}
          <Card>
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
              <CardDescription>
                The Kensys target price adjusts analyst consensus using fund intelligence
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0 text-blue-500">1</div>
                <div>
                  <strong>Holdings Analysis:</strong> Identify stocks held by skilled fund managers based on portfolio weight and market cap filters.
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0 text-blue-500">2</div>
                <div>
                  <strong>Fund Skill Signal:</strong> For each stock, aggregate the "gradient" (timing signal) from the top skilled funds using ia_skew and sector curves.
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0 text-blue-500">3</div>
                <div>
                  <strong>Sector Normalization:</strong> Calculate median_alpha = stock's analyst upside - sector median upside (removes sector bias).
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 text-emerald-500">4</div>
                <div>
                  <strong>Target Price Adjustment:</strong> Kensys Target = Analyst Median × (1 + delta), where delta = gradient - median_alpha.
                  Clipped to 80%-120% of analyst range.
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sector Distribution */}
          {stats?.sector_distribution?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Sector Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {stats.sector_distribution.map((s, idx) => (
                    <div key={idx} className="p-3 bg-muted/30 rounded">
                      <p className="text-xs text-muted-foreground truncate">{s.sector || "Unknown"}</p>
                      <p className="font-mono">{s.count?.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Target Prices Data Tab */}
        <TabsContent value="data" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">Observation Date</Label>
                  <select
                    className="w-full mt-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={obsDateFilter || ""}
                    onChange={(e) => setObsDateFilter(e.target.value || null)}
                  >
                    <option value="">All Dates</option>
                    {obsDates?.kensys_target_prices?.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">Symbol</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="e.g., AAPL"
                      value={searchSymbol}
                      onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                      className="flex-1"
                    />
                    <Button size="sm" onClick={fetchData} disabled={dataLoading}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-xs">Sector</Label>
                  <Input
                    placeholder="e.g., Technology"
                    value={searchSector}
                    onChange={(e) => setSectorFilter(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <Label className="text-xs">Min Upside (%)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 10"
                    onChange={(e) => setMinUpside(e.target.value ? Number(e.target.value) / 100 : null)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" size="sm" onClick={fetchData} disabled={dataLoading}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${dataLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card>
            <CardHeader>
              <CardTitle>Computed Target Prices ({targetPrices.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {targetPrices.length > 0 ? (
                <ScrollArea className="h-[500px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card border-b">
                      <tr>
                        <th className="text-left p-2">Symbol</th>
                        <th className="text-left p-2">Obs Date</th>
                        <th className="text-right p-2">Kensys Target</th>
                        <th className="text-right p-2">Upside</th>
                        <th className="text-right p-2">Current Price</th>
                        <th className="text-right p-2">Analyst Median</th>
                        <th className="text-left p-2">Sector</th>
                        <th className="text-right p-2">Funds</th>
                        <th className="text-right p-2">Gradient</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targetPrices.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-mono font-medium">{row.symbol}</td>
                          <td className="p-2 text-xs text-muted-foreground font-mono">{row.observation_date || "N/A"}</td>
                          <td className="p-2 text-right font-mono text-emerald-400">
                            {formatPrice(row.kensys_target_price)}
                          </td>
                          <td className="p-2 text-right">
                            <span className={`font-mono ${
                              row.kensys_upside >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {formatPercent(row.kensys_upside)}
                            </span>
                          </td>
                          <td className="p-2 text-right font-mono text-muted-foreground">
                            {formatPrice(row.price)}
                          </td>
                          <td className="p-2 text-right font-mono text-muted-foreground">
                            {formatPrice(row.target_median)}
                          </td>
                          <td className="p-2 text-xs text-muted-foreground">{row.sector || "N/A"}</td>
                          <td className="p-2 text-right font-mono">{row.n_funds || 0}</td>
                          <td className="p-2 text-right font-mono text-xs">
                            {row.gradient !== null ? row.gradient.toFixed(3) : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No target prices computed yet.</p>
                  <p className="text-sm mt-2">Run the pipeline to generate target prices.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Pipeline Logs</CardTitle>
                {runHistory.length > 0 && (
                  <select
                    className="p-2 rounded-md bg-background border text-sm"
                    value={selectedRunId}
                    onChange={(e) => {
                      setSelectedRunId(e.target.value);
                      fetchRunLogs(e.target.value);
                    }}
                  >
                    {runHistory.map((run) => (
                      <option key={run.run_id} value={run.run_id}>
                        {run.run_id} - {run.status} ({formatDate(run.started_at)})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {runLogs.length > 0 ? (
                <div className="h-[500px] overflow-auto border rounded-md p-2 bg-muted/10">
                  <div className="space-y-1 font-mono text-xs">
                    {runLogs.slice().reverse().map((log, idx) => (
                      <div 
                        key={idx} 
                        className={`p-2 rounded ${
                          log.level === "error" ? "bg-red-500/10 text-red-400" :
                          log.level === "warning" ? "bg-yellow-500/10 text-yellow-400" :
                          "bg-muted/30"
                        }`}
                      >
                        <span className="text-muted-foreground">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        <span className="mx-2">[{log.level.toUpperCase()}]</span>
                        <span>{log.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No logs available.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Run History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Run History</CardTitle>
            </CardHeader>
            <CardContent>
              {runHistory.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card border-b">
                      <tr>
                        <th className="text-left p-2">Run ID</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Started</th>
                        <th className="text-left p-2">Ended</th>
                        <th className="text-right p-2">Records</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runHistory.map((run, idx) => (
                        <tr 
                          key={idx} 
                          className={`border-b hover:bg-muted/50 cursor-pointer ${
                            selectedRunId === run.run_id ? 'bg-primary/10' : ''
                          }`}
                          onClick={() => {
                            setSelectedRunId(run.run_id);
                            setActiveTab("logs");
                          }}
                        >
                          <td className="p-2 font-mono">{run.run_id}</td>
                          <td className="p-2">
                            <Badge variant={run.is_test ? "outline" : "default"}>
                              {run.run_type}
                            </Badge>
                          </td>
                          <td className="p-2">
                            <Badge variant={
                              run.status === "completed" ? "default" :
                              run.status === "failed" ? "destructive" : "secondary"
                            }>
                              {run.status}
                            </Badge>
                          </td>
                          <td className="p-2 text-xs">{formatDate(run.started_at)}</td>
                          <td className="p-2 text-xs">{run.ended_at ? formatDate(run.ended_at) : "-"}</td>
                          <td className="p-2 text-right font-mono">
                            {run.result?.records_written?.toLocaleString() || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No runs yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dynamic Upside Tab */}
        <TabsContent value="dynamic" className="space-y-4">
          {/* Header Card with Actions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Dynamic Upside
                  </CardTitle>
                  <CardDescription>
                    Point-in-time upside using Kensys target prices and T-1 stock prices
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      fetchDynamicUpsideStats();
                      fetchDynamicUpsideData();
                      fetchDynamicUpsideDates();
                      fetchPriceFetchStatus();
                    }}
                    disabled={dynamicUpsideLoading}
                    data-testid="dynamic-upside-refresh-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${dynamicUpsideLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunDynamicUpside(false)}
                    disabled={dynamicUpsideRunning}
                    data-testid="dynamic-upside-run-today-btn"
                  >
                    <Play className={`w-4 h-4 mr-1 ${dynamicUpsideRunning ? 'animate-pulse' : ''}`} />
                    Run Today
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4">
              <p className="text-xs text-muted-foreground">Total Records</p>
              <p className="text-xl font-bold">{dynamicUpsideStats?.dynamic_upside?.total_records?.toLocaleString() || 0}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted-foreground">Symbols</p>
              <p className="text-xl font-bold">{dynamicUpsideStats?.dynamic_upside?.unique_symbols?.toLocaleString() || 0}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs text-muted-foreground">Date Range</p>
              <p className="text-sm font-mono">
                {dynamicUpsideStats?.dynamic_upside?.date_range?.oldest || "N/A"} → {dynamicUpsideStats?.dynamic_upside?.date_range?.newest || "N/A"}
              </p>
            </Card>
          </div>

          {/* Backfill Control Card */}
          <Card className="border-yellow-500/30 bg-yellow-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Database className="w-5 h-5 text-yellow-500" />
                Historical Backfill
              </CardTitle>
              <CardDescription>
                Compute dynamic upside for all historical dates using existing price data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => handleComputeFullDynamicUpside(false)}
                  disabled={backfillRunning}
                  className="bg-yellow-600 hover:bg-yellow-700"
                  data-testid="compute-dynamic-upside-btn"
                >
                  {backfillRunning ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Compute Dynamic Upside
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleComputeFullDynamicUpside(true)}
                  disabled={backfillRunning}
                  data-testid="force-recompute-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Force Recompute All
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Uses existing stock_perf_daily_raw prices. Runs in background - refresh stats to monitor.
              </p>
            </CardContent>
          </Card>

          {/* Recent Computation Dates */}
          {dynamicUpsideDates.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Recent Computation Dates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {dynamicUpsideDates.slice(0, 15).map((d) => (
                    <Badge key={d.date} variant="outline" className="text-xs py-1">
                      {d.date} <span className="text-muted-foreground ml-1">({d.record_count} stocks, avg {d.avg_upside_pct}%)</span>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ticker Search & History */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Search className="w-5 h-5" />
                Browse by Ticker
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Enter ticker symbol (e.g., AAPL)"
                  value={tickerSearch}
                  onChange={(e) => setTickerSearch(e.target.value.toUpperCase())}
                  className="max-w-xs font-mono"
                  data-testid="ticker-search-input"
                />
                <Button 
                  onClick={() => fetchTickerHistory(tickerSearch)}
                  disabled={!tickerSearch || tickerHistoryLoading}
                  data-testid="ticker-search-btn"
                >
                  {tickerHistoryLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
                {tickerSearch && (
                  <Button variant="ghost" size="sm" onClick={() => { setTickerSearch(""); setTickerHistory([]); }}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {tickerHistory.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 p-3 border-b">
                    <span className="font-mono font-bold text-lg">{tickerSearch}</span>
                    <span className="text-muted-foreground ml-2">({tickerHistory.length} records)</span>
                  </div>
                  <ScrollArea className="h-[300px]">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Date</th>
                          <th className="text-right p-2">Price</th>
                          <th className="text-right p-2">Target</th>
                          <th className="text-right p-2">Upside</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickerHistory.map((row, idx) => (
                          <tr key={idx} className="border-t hover:bg-muted/20">
                            <td className="p-2 font-mono text-xs">{row.observation_date}</td>
                            <td className="p-2 text-right font-mono">${row.current_price?.toFixed(2)}</td>
                            <td className="p-2 text-right font-mono text-emerald-400">${row.kensys_target_price?.toFixed(2)}</td>
                            <td className={`p-2 text-right font-mono font-medium ${row.dynamic_upside > 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {row.dynamic_upside > 0 ? '+' : ''}{(row.dynamic_upside * 100).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Top Upside Stocks (Latest)</CardTitle>
            </CardHeader>
            <CardContent>
              {dynamicUpsideLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : dynamicUpsideData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No dynamic upside data computed yet.</p>
                  <p className="text-sm mt-1">Click "Compute Dynamic Upside" to generate historical data.</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3">Symbol</th>
                        <th className="text-right p-3">Close (T-1)</th>
                        <th className="text-right p-3">Kensys Target</th>
                        <th className="text-right p-3">Dynamic Upside</th>
                        <th className="text-left p-3">Price Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dynamicUpsideData.slice(0, 25).map((row) => (
                        <tr key={row.symbol} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => setTickerSearch(row.symbol)}>
                          <td className="p-3 font-mono font-medium">{row.symbol}</td>
                          <td className="p-3 text-right font-mono">${row.current_price?.toFixed(2) || "N/A"}</td>
                          <td className="p-3 text-right font-mono text-emerald-400">${row.kensys_target_price?.toFixed(2) || "N/A"}</td>
                          <td className={`p-3 text-right font-medium font-mono ${row.dynamic_upside_pct > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {row.dynamic_upside_pct > 0 ? '+' : ''}{row.dynamic_upside_pct?.toFixed(1)}%
                          </td>
                          <td className="p-3 text-xs text-muted-foreground font-mono">{row.price_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Box */}
          <Card className="bg-blue-500/5 border-blue-500/20">
            <CardContent className="pt-4">
              <p className="text-blue-400 text-sm">
                <strong>Formula:</strong> Dynamic Upside = (Kensys Target Price / Closing Price at T-1) - 1
              </p>
              <p className="text-muted-foreground text-sm mt-2">
                Uses the <strong>closing price from the day before</strong> (T-1) for point-in-time accuracy.
                Target prices are from the most recent Kensys computation within a 4-month tolerance.
                This data is used by Strategies for point-in-time walk-forward backtesting.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Configuration</CardTitle>
              <CardDescription>
                Configure filters and parameters for target price computation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Market Cap Filters */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Market Cap Filters</h4>
                  <div>
                    <Label className="text-xs">Min Market Cap ($)</Label>
                    <Input
                      type="number"
                      value={config.mcap_min || 1000000000}
                      disabled
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Currently: ${((config.mcap_min || 1e9) / 1e9).toFixed(0)}B
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Max Market Cap ($)</Label>
                    <Input
                      type="number"
                      value={config.mcap_max || 1000000000000}
                      disabled
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Currently: ${((config.mcap_max || 1e12) / 1e12).toFixed(1)}T
                    </p>
                  </div>
                </div>

                {/* Portfolio Filters */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Portfolio Filters</h4>
                  <div>
                    <Label className="text-xs">Min Portfolio Weight</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={config.min_portfolio_weight || 0.001}
                      disabled
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Currently: {((config.min_portfolio_weight || 0.001) * 100).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Min Funds per Stock</Label>
                    <Input
                      type="number"
                      value={config.min_funds_per_stock || 2}
                      disabled
                      className="mt-1 font-mono"
                    />
                  </div>
                </div>

                {/* Target Calculation */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Target Calculation</h4>
                  <div>
                    <Label className="text-xs">Alpha Clip (+/-)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={config.alpha_clip || 0.4}
                      disabled
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Clip median_alpha at ±{((config.alpha_clip || 0.4) * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Target Clip Range</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        step="0.1"
                        value={config.target_clip_low || 0.8}
                        disabled
                        className="font-mono"
                        placeholder="Low"
                      />
                      <Input
                        type="number"
                        step="0.1"
                        value={config.target_clip_high || 1.2}
                        disabled
                        className="font-mono"
                        placeholder="High"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Clip to {((config.target_clip_low || 0.8) * 100).toFixed(0)}%-{((config.target_clip_high || 1.2) * 100).toFixed(0)}% of analyst range
                    </p>
                  </div>
                </div>

                {/* DB Settings */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Database Settings</h4>
                  <div>
                    <Label className="text-xs">Write Batch Size</Label>
                    <Input
                      type="number"
                      value={config.db_write_batch_size || 1000}
                      disabled
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Write Delay (ms)</Label>
                    <Input
                      type="number"
                      value={config.db_write_delay_ms || 1500}
                      disabled
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Delay between batches for MongoDB Atlas rate limiting
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg text-sm">
                <p className="text-muted-foreground">
                  <strong>Note:</strong> Configuration changes via API are not yet implemented.
                  Contact the administrator to modify these settings.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default KensysTargetsPage;
